'use client';

import { useMemo, useState } from 'react';
import { useLanguage } from '@/providers/LanguageProvider';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

import { FACT_NAME_LABEL_MAP } from '../display-page/fact-name-label-map';
import { Icons } from '../icons';
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';

interface FactsSelectProps {
  factName: string;
  factsData: { key: string; label: string }[];
  defaultFact: string;
  setDefaultFact: (factName: string) => void;
  setFactValue: (factName: string) => void;
}

const FactsSelect: React.FC<FactsSelectProps> = ({
  factName,
  factsData,
  defaultFact,
  setDefaultFact,
  setFactValue,
}) => {
  const [isFactSelectOpen, setIsFactSelectOpen] = useState(false);
  const { messages } = useLanguage();

  // Fact Label for the select dropdown
  const selectedFactLabel = useMemo(() => {
    const selectedFact = factsData.find((fact) => fact.key === factName);
    if (!selectedFact) return factName;

    return (
      FACT_NAME_LABEL_MAP?.[selectedFact.key]?.label ||
      selectedFact.label ||
      selectedFact.key
    );
  }, [factsData, factName]);

  // Handler to set default fact for the application
  const setDefaultFactForApp = (
    event: React.MouseEvent<HTMLButtonElement>,
    factName: string
  ) => {
    event.preventDefault();
    event.stopPropagation();
    setDefaultFact(factName);
  };

  return (
    <div>
      {factsData.length > 0 && (
        <div className="flex h-7 items-center rounded-e rounded-s border-2 border-form-control-border-rest px-3 py-1">
          <Select
            value={factName}
            open={isFactSelectOpen}
            onOpenChange={setIsFactSelectOpen}
            onValueChange={(newFactName) => setFactValue(newFactName)}
          >
            <SelectTrigger className="h-auto w-auto gap-1.5 border-none p-0 text-[13px] leading-[18px] text-body-subtle focus:ring-0 focus:ring-offset-0 [&>svg]:text-[#EBEFF1] [&>svg]:opacity-100">
              <SelectValue>{selectedFactLabel}</SelectValue>
            </SelectTrigger>
            <SelectContent className="overflow-visible rounded-none bg-white text-[#201F1E]">
              {[...factsData]?.map((fact: { key: string; label: string }) => {
                const customLabel =
                  FACT_NAME_LABEL_MAP?.[fact.key]?.label ||
                  fact.label ||
                  fact.key;
                return (
                  <SelectItem
                    key={fact.key}
                    value={fact.key}
                    className="group w-[250px] cursor-pointer focus:bg-[#EBEFF1] focus:text-[#201F1E]"
                  >
                    {customLabel}
                    {defaultFact !== fact.key && (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              type="button"
                              onPointerUp={(event) =>
                                setDefaultFactForApp(event, fact.key)
                              }
                              className="absolute right-1 top-1/2 hidden -translate-y-1/2 rounded-md text-[#257C9D] transition duration-150 hover:bg-[#257C9D1F] group-hover:inline-block"
                              aria-label={`Set ${customLabel} as default`}
                            >
                              {Icons.StarIcon('size-6')}
                            </button>
                          </TooltipTrigger>
                          <TooltipContent
                            hideWhenDetached
                            side="bottom"
                            className="!animate-none text-xs"
                          >
                            <TooltipArrow className="fill-popover" />
                            {messages?.ApplicationDetailPage?.set_as_default}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                    {isFactSelectOpen && defaultFact === fact.key && (
                      <span className="ml-2 inline-flex items-center gap-0.5 rounded-full bg-[#EBEFF166] px-2 py-0.5 text-xs font-medium text-[#8C9EA1]">
                        {Icons.StarFilledIcon('size-2.5')} Default
                      </span>
                    )}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>
      )}
    </div>
  );
};

export default FactsSelect;
